import React, { useState } from 'react';
import {
  Box, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindowsOutlined';
import TabletMacIcon from '@mui/icons-material/TabletMacOutlined';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphoneOutlined';
import SiteRenderer from 'dashboard/components/siteRenderer/SiteRenderer';
import DeviceFrame from './DeviceFrame';

/**
 * Live preview of the page as it stands, at a width you choose.
 *
 * Renders the same `<SiteRenderer />` the public site uses, so this is the
 * actual output rather than an approximation of it — and now at the actual
 * widths people read on, inside a real viewport (see DeviceFrame for why
 * that has to be an iframe).
 *
 * Desktop is deliberately "as wide as the panel" rather than a stated pixel
 * width: the editor is already on a desktop, and a 1280px frame squeezed
 * into a 700px panel would have to be scaled, which reintroduces exactly the
 * lying-about-size problem the frame exists to avoid.
 */

const DEVICES = [
  { key: 'desktop', label: 'Desktop', width: null, Icon: DesktopWindowsIcon, hint: 'As wide as the panel' },
  { key: 'tablet', label: 'Tablet', width: 834, Icon: TabletMacIcon, hint: '834px — iPad portrait' },
  { key: 'phone', label: 'Phone', width: 390, Icon: PhoneIphoneIcon, hint: '390px — iPhone 14' },
];

export default function PagePreviewPanel({ page }) {
  const [device, setDevice] = useState('desktop');
  const current = DEVICES.find((d) => d.key === device) || DEVICES[0];

  return (
    <Box
      mb='24px'
      sx={{
        bgcolor: 'action.hover',
        border: '1px solid', borderColor: 'divider',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction='row'
        sx={{
          px: '14px', py: '8px',
          bgcolor: 'background.paper',
          borderBottom: '1px solid', borderColor: 'divider',
        }}
        justifyContent='space-between' alignItems='center' gap='10px' flexWrap='wrap'
      >
        <Typography variant='caption' fontWeight='700' color='text.secondary' textTransform='uppercase'>
          Live preview
        </Typography>

        <Stack direction='row' alignItems='center' gap='10px' flexWrap='wrap'>
          <Typography variant='caption' color='text.secondary'>
            {current.width ? current.hint : `What visitors will see on /${page.slug}`}
          </Typography>
          <ToggleButtonGroup
            size='small'
            exclusive
            value={device}
            onChange={(_e, v) => v && setDevice(v)}
          >
            {DEVICES.map(({ key, label, Icon, hint }) => (
              <Tooltip key={key} title={`${label} — ${hint}`}>
                <ToggleButton value={key} aria-label={label} sx={{ px: '10px' }}>
                  <Icon fontSize='small' />
                </ToggleButton>
              </Tooltip>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </Stack>

      <Box
        sx={{
          p: current.width ? '16px' : 0,
          // A phone standing on the grey of the panel reads as a device;
          // flush to the edges it reads as a broken layout.
          bgcolor: current.width ? 'action.hover' : 'transparent',
          overflow: 'auto',
        }}
      >
        <Box
          sx={current.width ? {
            width: `${current.width}px`,
            maxWidth: '100%',
            mx: 'auto',
            borderRadius: '10px',
            overflow: 'hidden',
            boxShadow: (t) => (t.palette.mode === 'dark'
              ? '0 10px 30px -10px rgba(0,0,0,0.7)'
              : '0 10px 30px -12px rgba(16,24,40,0.35)'),
          } : {}}
        >
          <DeviceFrame width={current.width} height={600}>
            <SiteRenderer page={page} />
          </DeviceFrame>
        </Box>
      </Box>
    </Box>
  );
}
